import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type {
  TravelDetailsFormValues,
  TravelPersonalFormValues,
} from "@/D2C/features/travel-quote/redux/type";
import { RootState } from "../store/store";

export type QuoteFlowPaymentStatus = "IDLE" | "PROCESSING" | "SUCCESS" | "FAILED";
export type QuoteFlowPolicyResultStatus = "SUCCESS" | "SUCCESS_POLICY_FAILED" | "FAILED";

export type QuoteFlowPolicyResult = {
  paymentStatus: QuoteFlowPolicyResultStatus;
  apiStatus?: string;
  name?: string;
  email?: string;
  phone?: string;
  insurerName?: string;
  insurerLogo?: string;
  policyNumber?: string;
  policyPeriod?: string;
  policyStartDate?: string;
  policyEndDate?: string;
  policyTenureYears?: string | number;
  premium?: string | number;
  vat?: string | number;
  totalAmount?: string | number;
  failureReason?: string;
  referenceId?: string;
  currency?: string;
  addOns?: Array<{
    label: string;
    amount?: number;
    value?: number;
  }>;
};

export type QuoteFlowState = {
  quoteData: any | null;
  bindData: any | null;
  travelDetails: Partial<TravelDetailsFormValues> | null;
  personalDetails: Partial<TravelPersonalFormValues> | Record<string, any> | null;
  sessionData: any | null;
  otpVerificationRequired: boolean;
  otpVerificationComplete: boolean;
  moreInfoDraft: Record<string, any> | null;
  paymentStatus: QuoteFlowPaymentStatus;
  policyResult: QuoteFlowPolicyResult | null;
};

const initialState: QuoteFlowState = {
  quoteData: null,
  bindData: null,
  travelDetails: null,
  personalDetails: null,
  sessionData: null,
  otpVerificationRequired: false,
  otpVerificationComplete: false,
  moreInfoDraft: null,
  paymentStatus: "IDLE",
  policyResult: null,
};

const quoteFlowSlice = createSlice({
  name: "quoteFlow",
  initialState,
  reducers: {
    setQuoteFlowState(state, action: PayloadAction<Partial<QuoteFlowState>>) {
      Object.assign(state, action.payload);
    },
    setQuoteFlowQuoteData(state, action: PayloadAction<any | null>) {
      state.quoteData = action.payload;
    },
    setQuoteFlowBindData(state, action: PayloadAction<any | null>) {
      state.bindData = action.payload;
    },
    setQuoteFlowTravelDetails(
      state,
      action: PayloadAction<Partial<TravelDetailsFormValues> | null>
    ) {
      state.travelDetails = action.payload;
    },
    setQuoteFlowPersonalDetails(
      state,
      action: PayloadAction<Partial<TravelPersonalFormValues> | Record<string, any> | null>
    ) {
      state.personalDetails = action.payload;
    },
    setQuoteFlowSessionData(state, action: PayloadAction<any | null>) {
      state.sessionData = action.payload;
    },
    setQuoteFlowOtpVerificationRequired(state, action: PayloadAction<boolean>) {
      state.otpVerificationRequired = action.payload;
    },
    setQuoteFlowOtpVerificationComplete(state, action: PayloadAction<boolean>) {
      state.otpVerificationComplete = action.payload;
    },
    setQuoteFlowMoreInfoDraft(state, action: PayloadAction<Record<string, any> | null>) {
      state.moreInfoDraft = action.payload;
    },
    setQuoteFlowPaymentStatus(state, action: PayloadAction<QuoteFlowPaymentStatus>) {
      state.paymentStatus = action.payload;
    },
    setQuoteFlowPolicyResult(state, action: PayloadAction<QuoteFlowPolicyResult | null>) {
      state.policyResult = action.payload;
    },
    clearQuoteFlowPolicyResult(state) {
      state.policyResult = null;
    },
    clearQuoteFlowJourneyDataAfterPaymentSuccess(state) {
      state.otpVerificationRequired = false;
      state.otpVerificationComplete = false;
      state.moreInfoDraft = null;
      state.paymentStatus = "SUCCESS";
    },
    clearQuoteFlowState() {
      return initialState;
    },
  },
});

export const {
  clearQuoteFlowState,
  setQuoteFlowBindData,
  setQuoteFlowMoreInfoDraft,
  setQuoteFlowPaymentStatus,
  setQuoteFlowPolicyResult,
  setQuoteFlowPersonalDetails,
  setQuoteFlowQuoteData,
  setQuoteFlowOtpVerificationComplete,
  setQuoteFlowOtpVerificationRequired,
  setQuoteFlowSessionData,
  setQuoteFlowState,
  setQuoteFlowTravelDetails,
  clearQuoteFlowJourneyDataAfterPaymentSuccess,
  clearQuoteFlowPolicyResult,
} = quoteFlowSlice.actions;

export const selectQuoteFlowState = (state: RootState) => state.quoteFlow;

export default quoteFlowSlice.reducer;
